// ============================================================
// Variasi Kategori Kasir — CRUD template varian per kategori
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

function emptyVariantType(type = '', label = '', meta = {}) {
  const isFile = type === 'file';
  return {
    type,
    label,
    sort_order: 0,
    is_required: false,
    is_active: true,
    input_mode: meta.input_mode || (isFile ? 'file_or_link' : 'select'),
    accept_mimes: isFile ? (meta.accept_mimes || ['jpg', 'jpeg', 'tiff', 'pdf']) : [],
    max_file_kb: isFile ? (meta.max_file_kb || 10240) : null,
    options: [],
  };
}

function createKasirCategoryVariants() {
  return {
    hydrated: false,
    loading: false,
    saving: false,
    search: '',
    categories: [],
    variantTypeMeta: { types: [], presets: {}, file_config: null },
    selectedCategoryId: '',
    variantTypes: [],
    categoryInfo: null,
    newTypeLabel: '',

    init() {
      try {
        const raw = this.$el.getAttribute('data-config');
        if (raw) {
          const cfg = JSON.parse(raw);
          this.categories = cfg.categories || [];
          if (cfg.initialVariants?.length) {
            const first = cfg.initialVariants.find((c) => c.variant_types_count > 0) || cfg.initialVariants[0];
            if (first) {
              this.selectedCategoryId = first.id;
              this.variantTypes = this.cloneTypes(first.variant_types || []);
              this.categoryInfo = first;
            }
          }
        }
      } catch (e) {
        console.error('kasirCategoryVariants: gagal memuat config', e);
      }

      this.loadMeta();
      this.hydrated = true;

      if (!this.selectedCategoryId && this.categories.length) {
        this.selectCategory(this.categories[0].id);
      }
    },

    cloneTypes(types) {
      return (types || []).map((t, i) => {
        const isFile = t.type === 'file' || t.input_mode === 'file_or_link';
        return {
          type: t.type,
          label: t.label || t.type,
          sort_order: t.sort_order ?? i,
          is_required: !!t.is_required,
          is_active: t.is_active !== false,
          input_mode: t.input_mode || (isFile ? 'file_or_link' : 'select'),
          accept_mimes: Array.isArray(t.accept_mimes)
            ? t.accept_mimes
            : (typeof t.accept_mimes === 'string' && t.accept_mimes
              ? t.accept_mimes.split(',').map((s) => s.trim()).filter(Boolean)
              : (isFile ? ['jpg', 'jpeg', 'tiff', 'pdf'] : [])),
          max_file_kb: isFile ? (Number(t.max_file_kb) || 10240) : null,
          options: (t.options || []).map((o, j) => ({
            value: o.value,
            default_price_modifier: Number(o.default_price_modifier) || 0,
            specification: o.specification || '',
            sort_order: o.sort_order ?? j,
            is_active: o.is_active !== false,
          })),
        };
      });
    },

    async loadMeta() {
      try {
        const { data } = await axios.get('/api/v1/kasir/category-variants/meta/variant-types');
        this.variantTypeMeta = data.data || { types: [], presets: {}, file_config: null };
      } catch (e) {
        console.warn('Gagal memuat meta varian', e);
      }
    },

    async selectCategory(id) {
      if (!id) return;
      this.selectedCategoryId = id;
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/category-variants/' + id);
        this.categoryInfo = data.data?.category || null;
        this.variantTypes = this.cloneTypes(data.data?.variant_types || []);
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal memuat variasi kategori.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async fetchList() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/category-variants', {
          params: { search: this.search },
        });
        const list = data.data || [];
        if (this.selectedCategoryId) {
          const row = list.find((c) => c.id === this.selectedCategoryId);
          if (row) {
            this.categoryInfo = row;
            this.variantTypes = this.cloneTypes(row.variant_types || []);
          }
        }
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal memuat data.', 'error');
      } finally {
        this.loading = false;
      }
    },

    availableTypesForAdd() {
      const used = new Set(this.variantTypes.map((t) => t.type));
      return (this.variantTypeMeta.types || []).filter((t) => !used.has(t.type));
    },

    addVariantType(typeKey) {
      const meta = (this.variantTypeMeta.types || []).find((t) => t.type === typeKey);
      if (!meta) return;
      if (this.variantTypes.some((t) => t.type === meta.type)) {
        window.toast(`Jenis "${meta.label}" sudah ditambahkan.`, 'warning');
        return;
      }
      const row = emptyVariantType(meta.type, meta.label, {
        input_mode: meta.input_mode,
        accept_mimes: this.variantTypeMeta.file_config?.accept_mimes,
        max_file_kb: this.variantTypeMeta.file_config?.max_file_kb,
      });
      this.variantTypes.push(row);
      if (meta.type === 'file') {
        this.loadPreset(this.variantTypes.length - 1, 'file');
      }
    },

    slugifyType(text) {
      return String(text || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, '')
        .replace(/[\s-]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^anti_goreng$/, 'anti_gores')
        .slice(0, 50);
    },

    addCustomVariantType() {
      const label = this.newTypeLabel?.trim();
      if (!label) {
        window.toast('Nama jenis varian wajib diisi.', 'warning');
        return;
      }

      const type = this.slugifyType(label);
      if (!type || type.length < 2) {
        window.toast('Nama jenis varian tidak valid. Minimal 2 karakter (huruf/angka).', 'warning');
        return;
      }

      if (this.variantTypes.some((t) => t.type === type)) {
        window.toast(`Jenis "${label}" sudah ada.`, 'warning');
        return;
      }

      this.variantTypes.push(emptyVariantType(type, label));
      this.newTypeLabel = '';
    },

    onVariantTypeLabelChange(index) {
      const row = this.variantTypes[index];
      if (!row) return;
      if (!row.type || row._custom) {
        row.type = this.slugifyType(row.label);
      }
    },

    removeVariantType(index) {
      this.variantTypes.splice(index, 1);
    },

    addOption(typeIndex) {
      const t = this.variantTypes[typeIndex];
      if (!t) return;
      t.options.push({
        value: '',
        default_price_modifier: 0,
        specification: '',
        sort_order: t.options.length,
        is_active: true,
      });
    },

    removeOption(typeIndex, optIndex) {
      this.variantTypes[typeIndex]?.options.splice(optIndex, 1);
    },

    loadPreset(typeIndex, presetKey) {
      const preset = this.variantTypeMeta.presets?.[presetKey];
      const t = this.variantTypes[typeIndex];
      if (!preset?.length || !t) return;
      t.options = preset.map((o, i) => ({
        value: o.value,
        default_price_modifier: Number(o.default_price_modifier) || 0,
        specification: o.specification || '',
        sort_order: i,
        is_active: true,
      }));
    },

    isFileType(vt) {
      return vt?.type === 'file' || vt?.input_mode === 'file_or_link';
    },

    fileHint(vt) {
      const mimes = (vt?.accept_mimes || this.variantTypeMeta.file_config?.accept_mimes || ['jpg', 'jpeg', 'tiff', 'pdf'])
        .join(', ')
        .toUpperCase();
      const maxMb = Math.round((vt?.max_file_kb || this.variantTypeMeta.file_config?.max_file_kb || 10240) / 1024);
      return `${mimes} · maks. ${maxMb} MB, atau file berupa link/URL`;
    },

    typeLabel(type, rowLabel = '') {
      if (rowLabel) return rowLabel;
      return (this.variantTypeMeta.types || []).find((t) => t.type === type)?.label
        || String(type).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },

    isSuggestedType(type) {
      return (this.variantTypeMeta.types || []).some((t) => t.type === type);
    },

    totalOptions() {
      return this.variantTypes.reduce((n, t) => n + (t.options?.length || 0), 0);
    },

    async save() {
      if (!this.selectedCategoryId) {
        window.toast('Pilih kategori terlebih dahulu.', 'warning');
        return;
      }

      this.saving = true;
      const payload = {
        variant_types: this.variantTypes
          .filter((t) => (t.label?.trim() || t.type))
          .map((t, i) => {
            const type = this.slugifyType(t.type || t.label);
            const label = (t.label || '').trim() || this.typeLabel(type);
            const isFile = type === 'file';
            return {
              type,
              label,
              sort_order: t.sort_order ?? i,
              is_required: !!t.is_required,
              is_active: t.is_active !== false,
              input_mode: isFile ? 'file_or_link' : 'select',
              accept_mimes: isFile ? (t.accept_mimes || ['jpg', 'jpeg', 'tiff', 'pdf']) : null,
              max_file_kb: isFile ? (Number(t.max_file_kb) || 10240) : null,
              options: (t.options || [])
                .filter((o) => o.value?.trim())
                .map((o, j) => ({
                  value: o.value.trim(),
                  default_price_modifier: Number(o.default_price_modifier) || 0,
                  specification: o.specification?.trim() || null,
                  sort_order: o.sort_order ?? j,
                  is_active: o.is_active !== false,
                })),
            };
          }),
      };

      try {
        const { data } = await axios.put('/api/v1/kasir/category-variants/' + this.selectedCategoryId, payload);
        this.categoryInfo = data.data?.category || this.categoryInfo;
        this.variantTypes = this.cloneTypes(data.data?.variant_types || []);
        window.toast('Variasi kategori disimpan.', 'success');
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async clearAll() {
      const ok = await window.confirmDialog({
        title: 'Hapus Semua Variasi?',
        message: 'Semua jenis dan opsi varian untuk kategori ini akan dihapus.',
        confirmText: 'Ya, Hapus Semua',
        cancelText: 'Batal',
        variant: 'danger',
      });
      if (!ok) return;

      this.variantTypes = [];
      await this.save();
    },
  };
}

Alpine.data('kasirCategoryVariants', createKasirCategoryVariants);
